/**
 * The pristine-emission baseline behind delta emission.
 *
 * The save sweep collects every participating input's `__Record` and sibling join records into a
 * `<Context>.<Entity>.<GUID>` comprehension. Historically that whole collection was emitted, which
 * asserts load-time server state with last-writer-wins semantics — a document edited offline and
 * synced days later re-asserts its load snapshot over everything written in between.
 *
 * The baseline is the same collection, taken once the load transaction has settled: the
 * comprehension the form WOULD emit with no user action. Emission is then the difference between
 * the two, so a save carries what the user changed and nothing else.
 *
 * Capturing it by re-running the sweep — rather than by watching record writes — is what makes it
 * total. Roughly 40% of record mutations are plain property assignment on an object held by
 * reference, several writers are declarative (a trigger-group receiver whose `DataAddress` is
 * inside a `__Record`) and never enter this bundle at all, and some run server-side before the form
 * renders. No interception layer sees those. The sweep sees whatever they left behind, because it
 * reads state rather than observing writers.
 *
 * @module _EntityDataBaseline
 */

const libEntityFieldClassification = require('./_EntityFieldClassification.json');
const libScopedEntityData = require('./_ScopedEntityData.js');
const libSharedEntityRead = require('./_SharedEntityRead.js');

/** Where the baseline rides. Inside FormData so it survives an offline save/reload cycle. */
const FORMDATA_ADDRESS = 'AppData.DocumentData.ReportData.FormData';
/** The FormData key the baseline lives under; the writeback walk must not rewrite its leaves. */
const BASELINE_KEY = '__EntityDataBaseline';
const BASELINE_ADDRESS = libScopedEntityData.ENTITY_DATA_BASELINE_ADDRESS;

/** Columns the database and the meadow layer own; never a user change, never emitted. */
const AUDIT_COLUMNS = new Set(libEntityFieldClassification.AuditColumns || [ ]);

/** The per-entity identity / data column lists, generated from `lib/headlight_model`. */
const ENTITY_COLUMNS = libEntityFieldClassification.Entities || { };

/**
 * The field marking a record for deletion. Tombstones bypass the change test entirely — a delete is
 * an event the form minted deliberately, never something a diff should have to infer.
 */
const DELETED_FIELD = 'Deleted';

/**
 * References an update is refused without, by entity. See `isAlwaysEmittedReference` for how each
 * entry was established; everything not listed here is emitted only when it changed.
 */
const REQUIRED_REFERENCES =
{
	DocumentPolyJoin: [ 'IDDocument' ],
	Document: [ 'IDProject' ],
};

/**
 * A field name meadow reads as a directive rather than a column (`_GUID<Entity>` for an exact GUID
 * reference, `_Dest_` to retarget a foreign key). They are not columns, so the column filter must
 * not drop them, and they are not user data, so the change test must not run over them — they ride
 * along with whatever record they qualify.
 *
 * @param {string} pFieldName - The record field name.
 * @return {boolean} - True when the field is a meadow directive.
 */
function isDirectiveField(pFieldName)
{
	return typeof pFieldName === 'string' && (pFieldName.startsWith('_Dest_') || pFieldName.startsWith('_GUID'));
}

/**
 * A reference to ANOTHER entity's row, carried by GUID rather than by id.
 *
 * This is how a comprehension resolves a foreign key to a row that may not exist yet — the applier
 * sorts entities into dependency order by reading exactly these fields, so a `TestInstanceSampleJoin`
 * carrying `GUIDTestInstance`, or a `DocumentPolyJoin` carrying `GUIDSample`, is asserting the join's
 * subject. The model does not list them as columns of the entity they appear on, so they must be
 * recognised by shape or the column filter drops the foreign key and the row lands unattached.
 *
 * @param {string} pEntity - The entity the field appears on.
 * @param {string} pFieldName - The record field name.
 * @return {boolean} - True when the field points at another entity's row.
 */
function isCrossEntityReferenceField(pEntity, pFieldName)
{
	if (typeof pFieldName !== 'string')
	{
		return false;
	}
	if (pFieldName.indexOf('_Via_GUID') >= 0)
	{
		return true;
	}
	if (isIdentityField(pEntity, pFieldName))
	{
		return false;
	}
	return (/^GUID[A-Z]/).test(pFieldName) || (/^ID[A-Z]/).test(pFieldName);
}

/**
 * The generated column classification for an entity, or null when the model does not know it.
 *
 * @param {string} pEntity - The entity name.
 * @return {{ I: Array<string>, D: Array<string>, N?: Array<string> }|null} - The classification, or null.
 */
function getEntityClassification(pEntity)
{
	const tmpClassification = ENTITY_COLUMNS[pEntity];
	return (tmpClassification && typeof tmpClassification === 'object') ? tmpClassification : null;
}

/**
 * Is this field one of the entity's identity columns (`ID<Entity>` / `GUID<Entity>`)?
 *
 * Identity is carried on every record that survives the delta, because it is what the upsert
 * resolves the row by — it is never itself "a change the user made".
 *
 * @param {string} pEntity - The entity name.
 * @param {string} pFieldName - The record field name.
 * @return {boolean} - True when the field identifies the row.
 */
function isIdentityField(pEntity, pFieldName)
{
	const tmpClassification = getEntityClassification(pEntity);
	if (tmpClassification)
	{
		return tmpClassification.I.indexOf(pFieldName) >= 0;
	}
	return pFieldName === `ID${pEntity}` || pFieldName === `GUID${pEntity}`;
}

/**
 * Is this field an audit column?
 *
 * @param {string} pFieldName - The record field name.
 * @return {boolean} - True when the database owns the field.
 */
function isAuditField(pFieldName)
{
	return AUDIT_COLUMNS.has(pFieldName);
}

/**
 * Does this field participate in the change test — a real data column the user could have moved?
 *
 * Identity, audit, directives and `Deleted` are all excluded. So is any field the model does not
 * list as a column of this entity, which is how display-only stamps written onto a persisted join
 * shape (`SampleLineItemJoin.LineItemUnits` is the live case) stay out of both the diff and the
 * payload. An entity the model does not know falls back to "anything not otherwise excluded", so an
 * unmodelled projection target still emits rather than silently emitting nothing.
 *
 * @param {string} pEntity - The entity name.
 * @param {string} pFieldName - The record field name.
 * @return {boolean} - True when the field is comparable user data.
 */
function isComparableField(pEntity, pFieldName)
{
	if (typeof pFieldName !== 'string' || pFieldName.length < 1)
	{
		return false;
	}
	if (pFieldName === DELETED_FIELD || isDirectiveField(pFieldName) || isAuditField(pFieldName) || isIdentityField(pEntity, pFieldName))
	{
		return false;
	}
	if (isCrossEntityReferenceField(pEntity, pFieldName))
	{
		return true;
	}
	const tmpClassification = getEntityClassification(pEntity);
	if (!tmpClassification)
	{
		return true;
	}
	return tmpClassification.D.indexOf(pFieldName) >= 0;
}

/**
 * The references a write is refused without, by entity, even when they did not change. Everything
 * else — every other `ID<Entity>` / `GUID<Entity>` — is compared and emitted only when it moved.
 *
 * Both entries were established against QA with
 * `api/headlight/tools/probe-reference-field-requirements.js` (2026-09-23), which drove updates with
 * and without the unchanged references, at level 4 and level 1, over every entity these forms write:
 *
 * - `DocumentPolyJoin.IDDocument` — an update without it is refused, "Document not found, or you do
 *   not have rights to access this document", at both levels. The row's authorization reads it.
 * - `Document.IDProject` — an update is accepted without it; a CREATE is refused, "Cannot verify
 *   project membership: no IDProject was provided". A created record has no baseline and projects
 *   whole anyway, so this entry is belt and braces rather than load-bearing.
 *
 * Every other entity probed (`Sample`, `SampleLineItemJoin`, `SampleLabJoin`, `TestInstance`,
 * `TestInstanceSampleJoin`, `TestInstanceLabJoin`) accepted the update with no references at all.
 *
 * @param {string} pEntity - The entity name.
 * @param {string} pFieldName - The record field name.
 * @return {boolean} - True when the reference must ride every write.
 */
function isAlwaysEmittedReference(pEntity, pFieldName)
{
	if (!isCrossEntityReferenceField(pEntity, pFieldName))
	{
		return false;
	}
	const tmpRequired = REQUIRED_REFERENCES[pEntity];
	return !!tmpRequired && (tmpRequired.indexOf(pFieldName) >= 0);
}

/**
 * Does this field survive into an emitted record at all?
 *
 * Audit columns never do. Everything else does: identity because the upsert needs it, directives
 * because they qualify the identity, and data columns when they changed.
 *
 * @param {string} pEntity - The entity name.
 * @param {string} pFieldName - The record field name.
 * @return {boolean} - True when the field may be emitted.
 */
function isEmittableField(pEntity, pFieldName)
{
	if (typeof pFieldName !== 'string' || pFieldName.length < 1)
	{
		return false;
	}
	if (isAuditField(pFieldName))
	{
		return false;
	}
	if (pFieldName === DELETED_FIELD || isDirectiveField(pFieldName) || isIdentityField(pEntity, pFieldName) || isCrossEntityReferenceField(pEntity, pFieldName))
	{
		return true;
	}
	const tmpClassification = getEntityClassification(pEntity);
	if (!tmpClassification)
	{
		return true;
	}
	return tmpClassification.D.indexOf(pFieldName) >= 0;
}

/**
 * Whether a record carries a server-assigned identity, and is therefore server state rather than
 * something this form minted.
 *
 * Only such records may seed the baseline: a freshly synthesized record that has never been saved
 * has no baseline by definition, and treating one as its own baseline would suppress the very
 * emission that creates the row.
 *
 * @param {string} pEntity - The entity name.
 * @param {Record<string, any>} pRecord - The record.
 * @return {boolean} - True when the row exists server-side.
 */
function hasServerIdentity(pEntity, pRecord)
{
	if (!pRecord || typeof pRecord !== 'object' || Array.isArray(pRecord))
	{
		return false;
	}
	const tmpIdentity = pRecord[`ID${pEntity}`];
	if (tmpIdentity === null || typeof tmpIdentity === 'undefined' || tmpIdentity === '')
	{
		return false;
	}
	return Number(tmpIdentity) > 0;
}

/**
 * Is this leaf a tombstone rather than an upsert?
 *
 * @param {Record<string, any>} pRecord - The comprehension leaf.
 * @return {boolean} - True when the record asserts a delete.
 */
function isTombstoneRecord(pRecord)
{
	if (!pRecord || typeof pRecord !== 'object' || Array.isArray(pRecord))
	{
		return false;
	}
	const tmpDeleted = pRecord[DELETED_FIELD];
	return tmpDeleted === 1 || tmpDeleted === '1' || tmpDeleted === true;
}

/**
 * Is this field a numeric data column of the entity?
 *
 * @param {string} pEntity - The entity name.
 * @param {string} pFieldName - The record field name.
 * @return {boolean} - True when the model stores the field as a number.
 */
function isNumericField(pEntity, pFieldName)
{
	const tmpClassification = getEntityClassification(pEntity);
	return !!(tmpClassification && Array.isArray(tmpClassification.N) && tmpClassification.N.indexOf(pFieldName) >= 0);
}

/**
 * Compare two field values the way the wire does.
 *
 * REST hands numerics back as strings often enough that a strict compare would report a change on
 * every reload, so scalars compare loosely; null, undefined and empty string are one state, because
 * the three "empty" spellings move between the marshal, the fetch and the manifest with no user
 * involvement. Objects and arrays compare structurally. A numeric column compares by value when both
 * sides parse as numbers, because a decimal comes back at its column scale (`"1.000"`) while the form
 * holds what was typed (`"1"`).
 *
 * @param {any} pLeft - The baseline value.
 * @param {any} pRight - The current value.
 * @param {string} [pEntity] - The entity the field belongs to.
 * @param {string} [pFieldName] - The field name.
 * @return {boolean} - True when the values differ.
 */
function valuesDiffer(pLeft, pRight, pEntity, pFieldName)
{
	const tmpLeftEmpty = (pLeft === null || typeof pLeft === 'undefined' || pLeft === '');
	const tmpRightEmpty = (pRight === null || typeof pRight === 'undefined' || pRight === '');
	if (tmpLeftEmpty || tmpRightEmpty)
	{
		return tmpLeftEmpty !== tmpRightEmpty;
	}
	if (typeof pLeft === 'object' || typeof pRight === 'object')
	{
		return JSON.stringify(pLeft) !== JSON.stringify(pRight);
	}
	if (pEntity && pFieldName && isNumericField(pEntity, pFieldName))
	{
		const tmpLeftNumber = Number(pLeft);
		const tmpRightNumber = Number(pRight);
		if (Number.isFinite(tmpLeftNumber) && Number.isFinite(tmpRightNumber))
		{
			return tmpLeftNumber !== tmpRightNumber;
		}
	}
	// eslint-disable-next-line eqeqeq
	return pLeft != pRight;
}

/**
 * Reduce one emitted record to the fields that actually changed, plus the identity the upsert needs.
 *
 * A record with no baseline is one the form created, and projects whole — once. After its first
 * save it has a baseline and diffs like any other.
 *
 * A reference is compared like any other column and ships only when it moved, except for the few
 * `REQUIRED_REFERENCES` a write is refused without.
 *
 * A field the baseline carries and the record no longer does is NOT read as a clear. The record
 * builders drop keys constantly as they re-key a row between its GUID and ID forms, and a rebuild
 * that returns a narrower wrapper drops keys it never owned; none of those are a user clearing a
 * value. A value the user actually cleared arrives as an empty value, which does compare as a
 * change.
 *
 * @param {string} pEntity - The entity name.
 * @param {Record<string, any>|null} pBaseline - The baseline record, or null when there is none.
 * @param {Record<string, any>} pRecord - The record as the sweep collected it.
 * @return {Record<string, any>|null} - The record to emit, or null when nothing changed.
 */
function computeRecordDelta(pEntity, pBaseline, pRecord)
{
	if (!pRecord || typeof pRecord !== 'object' || Array.isArray(pRecord))
	{
		return null;
	}
	// A tombstone the baseline already carries was applied by an earlier save. Re-emitting it sends
	// the applier back to delete a row that is already gone.
	if (isTombstoneRecord(pRecord))
	{
		if (isTombstoneRecord(pBaseline))
		{
			return null;
		}
		return pRecord;
	}
	// The row this GUID named was deleted, so what sits here now is a new row with no history to
	// diff against - it has to project whole, exactly as a never-saved record does.
	const tmpBaseline = isTombstoneRecord(pBaseline) ? null : pBaseline;
	const tmpIdentityFields = { };
	const tmpChangedFields = { };
	let tmpChangedCount = 0;
	for (const tmpField of Object.keys(pRecord))
	{
		if (!isEmittableField(pEntity, tmpField))
		{
			continue;
		}
		if (!isComparableField(pEntity, tmpField))
		{
			tmpIdentityFields[tmpField] = pRecord[tmpField];
			continue;
		}
		const tmpChanged = (!tmpBaseline || valuesDiffer(tmpBaseline[tmpField], pRecord[tmpField], pEntity, tmpField));
		if (isAlwaysEmittedReference(pEntity, tmpField))
		{
			tmpIdentityFields[tmpField] = pRecord[tmpField];
			if (tmpChanged)
			{
				++tmpChangedCount;
			}
			continue;
		}
		if (tmpChanged)
		{
			tmpChangedFields[tmpField] = pRecord[tmpField];
			++tmpChangedCount;
		}
	}
	if (!tmpBaseline)
	{
		return Object.assign(tmpIdentityFields, tmpChangedFields);
	}
	if (tmpChangedCount < 1)
	{
		return null;
	}
	return Object.assign(tmpIdentityFields, tmpChangedFields);
}

/**
 * Strip a record down to what may be stored as a baseline: emittable fields only, so the baseline
 * and the emission are compared on the same terms. Object values (JSON columns) are copied: a
 * baseline sharing the form's live object moves with every edit inside it, so the edit never diffs.
 *
 * @param {string} pEntity - The entity name.
 * @param {Record<string, any>} pRecord - The record to reduce.
 * @return {Record<string, any>} - The reduced record.
 */
function reduceRecordForBaseline(pEntity, pRecord)
{
	const tmpReduced = { };
	if (!pRecord || typeof pRecord !== 'object' || Array.isArray(pRecord))
	{
		return tmpReduced;
	}
	for (const tmpField of Object.keys(pRecord))
	{
		if (isEmittableField(pEntity, tmpField))
		{
			const tmpValue = pRecord[tmpField];
			tmpReduced[tmpField] = (tmpValue && typeof tmpValue === 'object') ? JSON.parse(JSON.stringify(tmpValue)) : tmpValue;
		}
	}
	return tmpReduced;
}

/**
 * The stored baseline tree, shaped exactly like the comprehension it is compared against
 * (`<Context>.<Entity>.<GUID>`) so a bad emission can be read against it directly.
 *
 * @param {Object} pPict - The pict instance.
 * @param {boolean} [pCreate] - Create the store when it is absent.
 * @return {Record<string, any>|null} - The store, or null when it does not exist and was not created.
 */
function getBaselineStore(pPict, pCreate)
{
	if (!pPict || !pPict.manifest || typeof pPict.manifest.getValueAtAddress !== 'function')
	{
		return null;
	}
	const tmpStore = pPict.manifest.getValueAtAddress(pPict, BASELINE_ADDRESS);
	if (tmpStore && typeof tmpStore === 'object' && !Array.isArray(tmpStore))
	{
		return tmpStore;
	}
	if (!pCreate || typeof pPict.manifest.setValueAtAddress !== 'function')
	{
		return null;
	}
	const tmpCreated = { };
	pPict.manifest.setValueAtAddress(pPict, BASELINE_ADDRESS, tmpCreated);
	return tmpCreated;
}

/**
 * Read one baseline record.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pContext - The comprehension context.
 * @param {string} pEntity - The entity name.
 * @param {string} pGUID - The record's external GUID.
 * @return {Record<string, any>|null} - The baseline record, or null when there is none.
 */
function readBaselineRecord(pPict, pContext, pEntity, pGUID)
{
	const tmpStore = getBaselineStore(pPict, false);
	if (!tmpStore)
	{
		return null;
	}
	const tmpContextNode = tmpStore[pContext];
	if (!tmpContextNode || typeof tmpContextNode !== 'object')
	{
		return null;
	}
	const tmpBucket = tmpContextNode[pEntity];
	if (!tmpBucket || typeof tmpBucket !== 'object')
	{
		return null;
	}
	const tmpRecord = tmpBucket[pGUID];
	return (tmpRecord && typeof tmpRecord === 'object' && !Array.isArray(tmpRecord)) ? tmpRecord : null;
}

/**
 * Write one baseline record.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pContext - The comprehension context.
 * @param {string} pEntity - The entity name.
 * @param {string} pGUID - The record's external GUID.
 * @param {Record<string, any>} pRecord - The record to store.
 * @return {boolean} - True when the record was stored.
 */
function writeBaselineRecord(pPict, pContext, pEntity, pGUID, pRecord)
{
	if (!pContext || !pEntity || !pGUID)
	{
		return false;
	}
	const tmpStore = getBaselineStore(pPict, true);
	if (!tmpStore)
	{
		return false;
	}
	if (!tmpStore[pContext] || typeof tmpStore[pContext] !== 'object' || Array.isArray(tmpStore[pContext]))
	{
		tmpStore[pContext] = { };
	}
	if (!tmpStore[pContext][pEntity] || typeof tmpStore[pContext][pEntity] !== 'object' || Array.isArray(tmpStore[pContext][pEntity]))
	{
		tmpStore[pContext][pEntity] = { };
	}
	tmpStore[pContext][pEntity][pGUID] = reduceRecordForBaseline(pEntity, pRecord);
	return true;
}

/**
 * Whether a record has a change staged under a context that the server has not applied yet.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pContext - The comprehension context.
 * @param {string} pEntity - The entity name.
 * @param {string} pGUID - The record's external GUID.
 * @return {boolean} - True when a leaf for the record is staged.
 */
function isRecordStaged(pPict, pContext, pEntity, pGUID)
{
	let tmpStaged = libScopedEntityData.resolveDestination(pPict);
	for (const tmpPart of `${pContext}.${pEntity}`.split('.'))
	{
		tmpStaged = (tmpStaged && typeof tmpStaged === 'object') ? tmpStaged[tmpPart] : null;
	}
	return !!(tmpStaged && typeof tmpStaged === 'object' && tmpStaged[pGUID]);
}

/**
 * Seed the baseline from a settled load sweep.
 *
 * Only records carrying a server identity are stored. A record the form synthesized during the load
 * — an init auto-create, a join minted for a row that has none — is not server state; baselining it
 * would suppress the very emission that creates the row.
 *
 * The first capture of a session REPLACES the contexts the sweep produced wholesale, because at that
 * point the sweep is the authority on the server state the form loaded. Contexts it did not touch are
 * left alone. Every render transaction schedules a capture, and the sweep reads the form's live
 * records, so a later one also reads the user's unsaved edits: refreshing an existing entry from it
 * would make the next save drop the edit. Later captures therefore only add records the session has
 * never baselined, and a baselined record moves only with a save's fold.
 *
 * Neither path may baseline a record with a change staged under the context: the form holds that change
 * and the server does not, so it would read as already written and the next save's prune would drop it.
 *
 * @param {Object} pPict - The pict instance.
 * @param {Map<string, Record<string, any>>} pContextRoots - The `<Entity>.<GUID>` roots the sweep filled, by context.
 * @return {number} - How many records were baselined.
 */
function captureBaselineFromSweep(pPict, pContextRoots)
{
	if (!pContextRoots || typeof pContextRoots.forEach !== 'function')
	{
		return 0;
	}
	const tmpStore = getBaselineStore(pPict, true);
	if (!tmpStore)
	{
		return 0;
	}
	const tmpSession = getCaptureSession(pPict, tmpStore);
	if (tmpSession)
	{
		return addUnknownRecordsToBaseline(pPict, tmpSession, pContextRoots);
	}
	let tmpCount = 0;
	let tmpCarriedCount = 0;
	pContextRoots.forEach((pContextRoot, pContext) =>
	{
		const tmpPrevious = (tmpStore[pContext] && typeof tmpStore[pContext] === 'object') ? tmpStore[pContext] : { };
		tmpStore[pContext] = { };
		if (!pContextRoot || typeof pContextRoot !== 'object' || Array.isArray(pContextRoot))
		{
			return;
		}
		for (const tmpEntity of Object.keys(pContextRoot))
		{
			const tmpBucket = pContextRoot[tmpEntity];
			if (!tmpBucket || typeof tmpBucket !== 'object' || Array.isArray(tmpBucket))
			{
				continue;
			}
			for (const tmpGUID of Object.keys(tmpBucket))
			{
				const tmpRecord = tmpBucket[tmpGUID];
				if (!hasServerIdentity(tmpEntity, tmpRecord) || isTombstoneRecord(tmpRecord))
				{
					// The sweep cannot prove this record, so it cannot refresh its baseline - but it must not
					// destroy one either. A `DocumentPolyJoin` or `TestInstanceLabJoin` is never re-fetched, so
					// the post-save fold is the only thing that will ever baseline it; wiping here would make it
					// project whole on every save for the life of the document. Carrying it forward is safe for
					// exactly these records: nothing but this form writes them.
					continue;
				}
				if (isRecordStaged(pPict, pContext, tmpEntity, tmpGUID))
				{
					// Left to the carry-forward below, or to project whole when it has no baseline yet.
					continue;
				}
				if (!tmpStore[pContext][tmpEntity] || typeof tmpStore[pContext][tmpEntity] !== 'object')
				{
					tmpStore[pContext][tmpEntity] = { };
				}
				tmpStore[pContext][tmpEntity][tmpGUID] = reduceRecordForBaseline(tmpEntity, tmpRecord);
				++tmpCount;
			}
		}
		// Absence from the sweep is not evidence of removal: the test-instance triple and the join
		// collections hydrate asynchronously, so a record can simply not have arrived yet. Anything the
		// sweep could not re-derive keeps the baseline it had - a stale entry for a record the form truly
		// dropped is inert, because a record the form no longer holds is never emitted either.
		for (const tmpEntity of Object.keys(tmpPrevious))
		{
			const tmpPreviousBucket = tmpPrevious[tmpEntity];
			if (!tmpPreviousBucket || typeof tmpPreviousBucket !== 'object')
			{
				continue;
			}
			for (const tmpGUID of Object.keys(tmpPreviousBucket))
			{
				if (tmpStore[pContext][tmpEntity] && tmpStore[pContext][tmpEntity][tmpGUID])
				{
					continue;
				}
				if (!tmpStore[pContext][tmpEntity] || typeof tmpStore[pContext][tmpEntity] !== 'object')
				{
					tmpStore[pContext][tmpEntity] = { };
				}
				tmpStore[pContext][tmpEntity][tmpGUID] = tmpPreviousBucket[tmpGUID];
				++tmpCarriedCount;
			}
		}
	});
	const tmpNewSession = { Store: tmpStore, Known: new Set() };
	markStoredRecordsKnown(tmpNewSession, tmpStore);
	getBaselineAppState(pPict).CaptureSession = tmpNewSession;
	if (pPict.log && typeof pPict.log.trace === 'function')
	{
		pPict.log.trace(`EntityDataBaseline: capture stored ${tmpCount} record(s) and carried ${tmpCarriedCount} the sweep could not prove.`);
	}
	return tmpCount;
}

/**
 * The key a baseline record is known by within a capture session.
 *
 * @param {string} pContext - The comprehension context.
 * @param {string} pEntity - The entity name.
 * @param {string} pGUID - The record's external GUID.
 * @return {string} - The key.
 */
function getBaselineRecordKey(pContext, pEntity, pGUID)
{
	return `${pContext}\n${pEntity}\n${pGUID}`;
}

/**
 * The capture session a first load capture opened on this baseline store, or null when there is none.
 *
 * Keyed on the store object: opening a document, or reopening this one, hands FormData a new store,
 * and the new store's first capture refreshes from the server state that load fetched.
 *
 * @param {Object} pPict - The pict instance.
 * @param {Record<string, any>|null} pStore - The baseline store.
 * @return {{ Store: Record<string, any>, Known: Set<string> }|null} - The session, or null.
 */
function getCaptureSession(pPict, pStore)
{
	const tmpAppState = getBaselineAppState(pPict);
	const tmpSession = tmpAppState ? tmpAppState.CaptureSession : null;
	return (tmpSession && pStore && tmpSession.Store === pStore) ? tmpSession : null;
}

/**
 * Whether a first load capture has already seeded the current baseline store.
 *
 * @param {Object} pPict - The pict instance.
 * @return {boolean} - True when later captures only add records.
 */
function hasCaptureSession(pPict)
{
	return !!getCaptureSession(pPict, getBaselineStore(pPict, false));
}

/**
 * Mark every record the store holds as known to the session.
 *
 * @param {{ Store: Record<string, any>, Known: Set<string> }} pSession - The capture session.
 * @param {Record<string, any>} pStore - The baseline store.
 * @return {void}
 */
function markStoredRecordsKnown(pSession, pStore)
{
	for (const tmpContext of Object.keys(pStore))
	{
		const tmpContextNode = pStore[tmpContext];
		if (!tmpContextNode || typeof tmpContextNode !== 'object' || Array.isArray(tmpContextNode))
		{
			continue;
		}
		for (const tmpEntity of Object.keys(tmpContextNode))
		{
			const tmpBucket = tmpContextNode[tmpEntity];
			if (!tmpBucket || typeof tmpBucket !== 'object' || Array.isArray(tmpBucket))
			{
				continue;
			}
			for (const tmpGUID of Object.keys(tmpBucket))
			{
				pSession.Known.add(getBaselineRecordKey(tmpContext, tmpEntity, tmpGUID));
			}
		}
	}
}

/**
 * A capture after the session's first: baseline only records the session has never known, the ones
 * that hydrated late, and leave every known record's baseline where the load or a save put it.
 *
 * A record a save released (a failed PUT clears the store; an unapplied record is dropped) stays known,
 * so it keeps projecting whole instead of being re-seeded from the form's unsaved state.
 *
 * @param {Object} pPict - The pict instance.
 * @param {{ Store: Record<string, any>, Known: Set<string> }} pSession - The capture session.
 * @param {Map<string, Record<string, any>>} pContextRoots - The `<Entity>.<GUID>` roots the sweep filled, by context.
 * @return {number} - How many records were added.
 */
function addUnknownRecordsToBaseline(pPict, pSession, pContextRoots)
{
	markStoredRecordsKnown(pSession, pSession.Store);
	let tmpAdded = 0;
	let tmpKnown = 0;
	pContextRoots.forEach((pContextRoot, pContext) =>
	{
		if (!pContextRoot || typeof pContextRoot !== 'object' || Array.isArray(pContextRoot))
		{
			return;
		}
		for (const tmpEntity of Object.keys(pContextRoot))
		{
			const tmpBucket = pContextRoot[tmpEntity];
			if (!tmpBucket || typeof tmpBucket !== 'object' || Array.isArray(tmpBucket))
			{
				continue;
			}
			for (const tmpGUID of Object.keys(tmpBucket))
			{
				const tmpRecord = tmpBucket[tmpGUID];
				if (!hasServerIdentity(tmpEntity, tmpRecord) || isTombstoneRecord(tmpRecord))
				{
					continue;
				}
				const tmpKey = getBaselineRecordKey(pContext, tmpEntity, tmpGUID);
				if (pSession.Known.has(tmpKey))
				{
					++tmpKnown;
					continue;
				}
				pSession.Known.add(tmpKey);
				if (isRecordStaged(pPict, pContext, tmpEntity, tmpGUID))
				{
					continue;
				}
				writeBaselineRecord(pPict, pContext, tmpEntity, tmpGUID, tmpRecord);
				++tmpAdded;
			}
		}
	});
	if (pPict.log && typeof pPict.log.trace === 'function')
	{
		pPict.log.trace(`EntityDataBaseline: later capture added ${tmpAdded} record(s) first seen after the load and left ${tmpKnown} known record(s) as the load or a save baselined them.`);
	}
	return tmpAdded;
}

/**
 * Re-baseline named records from what the form holds now, for a path that has just replaced them
 * with a fresh server row (the "out of date" refresh), so the server's own values do not read as a
 * user edit. Only those records move; every other record keeps its baseline, unsaved edits included.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pEntity - The entity name.
 * @param {Array<string>} pGUIDs - The external GUIDs of the replaced records.
 * @return {number} - How many baseline records were refreshed.
 */
function refreshBaselineRecords(pPict, pEntity, pGUIDs)
{
	if (!pEntity || !Array.isArray(pGUIDs) || pGUIDs.length < 1)
	{
		return 0;
	}
	const tmpContextRoots = runProjectionSweepToScratch(pPict);
	if (!tmpContextRoots)
	{
		return 0;
	}
	const tmpStore = getBaselineStore(pPict, true);
	if (!tmpStore)
	{
		return 0;
	}
	const tmpSession = getCaptureSession(pPict, tmpStore);
	const tmpGUIDs = new Set(pGUIDs);
	let tmpCount = 0;
	tmpContextRoots.forEach((pContextRoot, pContext) =>
	{
		libScopedEntityData.sanitizeContextRoot(pContextRoot);
		const tmpBucket = (pContextRoot && typeof pContextRoot === 'object') ? pContextRoot[pEntity] : null;
		if (!tmpBucket || typeof tmpBucket !== 'object' || Array.isArray(tmpBucket))
		{
			return;
		}
		for (const tmpGUID of Object.keys(tmpBucket))
		{
			const tmpRecord = tmpBucket[tmpGUID];
			if (!tmpGUIDs.has(tmpGUID) || !hasServerIdentity(pEntity, tmpRecord) || isTombstoneRecord(tmpRecord))
			{
				continue;
			}
			writeBaselineRecord(pPict, pContext, pEntity, tmpGUID, tmpRecord);
			if (tmpSession)
			{
				tmpSession.Known.add(getBaselineRecordKey(pContext, pEntity, tmpGUID));
			}
			++tmpCount;
		}
	});
	if (pPict.log && typeof pPict.log.trace === 'function')
	{
		pPict.log.trace(`EntityDataBaseline: re-baselined ${tmpCount} ${pEntity} record(s) from a server refresh.`);
	}
	return tmpCount;
}

/**
 * @typedef {Object} RecordRefreshDecision
 * @property {'replace'|'adopt'|'keep'} Action - `replace`: take the row, baseline untouched. `adopt`: take
 *   the row and move `Fields` into the baseline. `keep`: hold on to the form's record.
 * @property {Array<string>} Fields - For `adopt`, the fields that move into the baseline; for `keep`, the fields in conflict.
 * @property {string} Reason - Why, for the log line.
 */

/**
 * Decide how a server row that is about to replace a record the form holds may be taken.
 *
 * Only the load capture that opens a session may set a record's baseline. A later refresh of the same
 * record — the open-time re-fetch landing after another render already captured — would otherwise leave
 * the baseline on the snapshot the document last saved, and every field another actor moved since would
 * ship as a user edit. So the refresh reconciles the baseline itself, but never at the cost of a change
 * the server does not have yet:
 *
 * - a leaf still staged for this record under the context (an offline save, or one armed after a failed
 *   apply) or a newest logged outcome that failed: keep the held record;
 * - a field the row would overwrite where the held value differs from the baseline (an edit not yet
 *   saved): keep the held record;
 * - otherwise adopt the row, and move into the baseline exactly the fields the server moved, plus any
 *   field the form already holds at the server's value (copied in after the capture), which would
 *   otherwise be sent again.
 *
 * A record with no baseline has nothing stale to correct and is replaced as before.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pContext - The context the record projects under.
 * @param {string} pEntity - The entity name.
 * @param {string} pGUID - The record's external GUID.
 * @param {Record<string, any>} pHeldRecord - The record the form holds now.
 * @param {Record<string, any>} pFetchedRecord - The server row about to replace it.
 * @return {RecordRefreshDecision} - The decision.
 */
function classifyRecordRefresh(pPict, pContext, pEntity, pGUID, pHeldRecord, pFetchedRecord)
{
	/** @type {RecordRefreshDecision} */
	const tmpReplace = { Action: 'replace', Fields: [ ], Reason: 'no baseline to reconcile' };
	if (!pGUID || !pHeldRecord || typeof pHeldRecord !== 'object' || Array.isArray(pHeldRecord) || !pFetchedRecord || typeof pFetchedRecord !== 'object' || Array.isArray(pFetchedRecord))
	{
		return tmpReplace;
	}
	const tmpBaseline = readBaselineRecord(pPict, pContext, pEntity, pGUID);
	if (!tmpBaseline || isTombstoneRecord(tmpBaseline))
	{
		return tmpReplace;
	}
	const tmpDestination = libScopedEntityData.resolveDestination(pPict);
	if (isRecordStaged(pPict, pContext, pEntity, pGUID))
	{
		return { Action: 'keep', Fields: [ ], Reason: `a change is still staged under ${pContext}` };
	}
	const tmpLog = (tmpDestination && Array.isArray(tmpDestination.ExecutionLog)) ? tmpDestination.ExecutionLog : [ ];
	let tmpLatestOutcome = null;
	for (const tmpEntry of tmpLog)
	{
		if (!tmpEntry || typeof tmpEntry !== 'object' || tmpEntry.TriggerKey !== pContext)
		{
			continue;
		}
		const tmpOutcome = readLogEntryOutcomes(tmpEntry).get(`${pEntity} ${pGUID}`);
		if (tmpOutcome)
		{
			tmpLatestOutcome = tmpOutcome;
		}
	}
	if (tmpLatestOutcome && tmpLatestOutcome.Failed)
	{
		return { Action: 'keep', Fields: [ ], Reason: 'the server logged its last save as not applied' };
	}
	const tmpConflicts = [ ];
	const tmpMoved = [ ];
	const tmpAlreadyHeld = [ ];
	for (const tmpField of Object.keys(pFetchedRecord))
	{
		if (!isEmittableField(pEntity, tmpField) || !isComparableField(pEntity, tmpField))
		{
			continue;
		}
		if (!valuesDiffer(pHeldRecord[tmpField], pFetchedRecord[tmpField], pEntity, tmpField))
		{
			if (valuesDiffer(tmpBaseline[tmpField], pFetchedRecord[tmpField], pEntity, tmpField))
			{
				tmpAlreadyHeld.push(tmpField);
			}
			continue;
		}
		if (valuesDiffer(tmpBaseline[tmpField], pHeldRecord[tmpField], pEntity, tmpField))
		{
			tmpConflicts.push(tmpField);
		}
		else
		{
			tmpMoved.push(tmpField);
		}
	}
	if (tmpConflicts.length > 0)
	{
		return { Action: 'keep', Fields: tmpConflicts, Reason: `the row would overwrite unsaved changes to ${tmpConflicts.join(', ')}` };
	}
	const tmpReasons = [ ];
	if (tmpMoved.length > 0)
	{
		tmpReasons.push(`the server moved ${tmpMoved.join(', ')}`);
	}
	if (tmpAlreadyHeld.length > 0)
	{
		tmpReasons.push(`the form already held the server's ${tmpAlreadyHeld.join(', ')}`);
	}
	return { Action: 'adopt', Fields: tmpMoved.concat(tmpAlreadyHeld), Reason: tmpReasons.length > 0 ? tmpReasons.join('; ') : 'the row matches the baseline' };
}

/**
 * Move the named fields of a server row into a record's baseline, leaving every other field as it was.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pContext - The comprehension context.
 * @param {string} pEntity - The entity name.
 * @param {string} pGUID - The record's external GUID.
 * @param {Record<string, any>} pFetchedRecord - The server row.
 * @param {Array<string>} pFields - The fields to move.
 * @return {boolean} - True when the baseline changed.
 */
function adoptRefreshedFields(pPict, pContext, pEntity, pGUID, pFetchedRecord, pFields)
{
	const tmpBaseline = readBaselineRecord(pPict, pContext, pEntity, pGUID);
	if (!tmpBaseline || !Array.isArray(pFields) || pFields.length < 1)
	{
		return false;
	}
	const tmpMerged = Object.assign({ }, tmpBaseline);
	for (const tmpField of pFields)
	{
		tmpMerged[tmpField] = pFetchedRecord[tmpField];
	}
	return writeBaselineRecord(pPict, pContext, pEntity, pGUID, tmpMerged);
}

/**
 * Fold a completed save's emission into the baseline, so the next save diffs against what was
 * actually written rather than re-asserting it.
 *
 * Called from `onAfterSaveDataAsync`, which the save waterfall only reaches when every earlier step
 * succeeded — so reaching here is the client's evidence that the document PUT carrying this
 * comprehension went through. That gate is the reason the fold is not done at prune time: advancing
 * the baseline over a save that then failed would make the retry emit nothing and lose the edit.
 *
 * Unlike the load capture this does NOT require a server identity: a record the form just created
 * has none until the document is reloaded, and it is exactly the record that must stop re-emitting
 * in full on every subsequent save.
 *
 * @param {Object} pPict - The pict instance.
 * @param {Map<string, Record<string, any>>} pContextRoots - The emitted `<Entity>.<GUID>` roots, by context.
 * @return {number} - How many records were folded in.
 */
function foldEmissionIntoBaseline(pPict, pContextRoots)
{
	const tmpFolded = [];
	if (!pContextRoots || typeof pContextRoots.forEach !== 'function')
	{
		return 0;
	}
	let tmpCount = 0;
	pContextRoots.forEach((pContextRoot, pContext) =>
	{
		if (!pContextRoot || typeof pContextRoot !== 'object' || Array.isArray(pContextRoot))
		{
			return;
		}
		for (const tmpEntity of Object.keys(pContextRoot))
		{
			const tmpBucket = pContextRoot[tmpEntity];
			if (!tmpBucket || typeof tmpBucket !== 'object' || Array.isArray(tmpBucket))
			{
				continue;
			}
			for (const tmpGUID of Object.keys(tmpBucket))
			{
				const tmpRecord = tmpBucket[tmpGUID];
				if (!tmpRecord || typeof tmpRecord !== 'object' || Array.isArray(tmpRecord))
				{
					continue;
				}
				if (isTombstoneRecord(tmpRecord))
				{
					writeBaselineRecord(pPict, pContext, tmpEntity, tmpGUID, reduceRecordForBaseline(tmpEntity, tmpRecord));
				tmpFolded.push(`${pContext}/${tmpEntity}#tombstone`);
				++tmpCount;
				continue;
				}
				const tmpExisting = readBaselineRecord(pPict, pContext, tmpEntity, tmpGUID);
				// A record folded over a tombstone is a re-add at the same GUID, so it replaces rather than
				// merges - carrying the tombstone's fields forward would leave `Deleted` in the baseline.
				const tmpPrior = isTombstoneRecord(tmpExisting) ? { } : (tmpExisting || { });
				const tmpMerged = Object.assign({ }, tmpPrior, reduceRecordForBaseline(tmpEntity, tmpRecord));
				writeBaselineRecord(pPict, pContext, tmpEntity, tmpGUID, tmpMerged);
				tmpFolded.push(`${pContext}/${tmpEntity}`);
				++tmpCount;
			}
		}
	});
	if (pPict && pPict.log && typeof pPict.log.trace === 'function')
	{
		pPict.log.trace(`EntityDataBaseline: folded ${tmpCount} record(s) into the baseline: ${tmpFolded.join(', ') || 'none'}.`);
	}
	return tmpCount;
}

/**
 * Forget one baseline record — a deleted row has no baseline, and a GUID re-added after a tombstone
 * must project whole again rather than diff against the row it replaced.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pContext - The comprehension context.
 * @param {string} pEntity - The entity name.
 * @param {string} pGUID - The record's external GUID.
 * @return {boolean} - True when a record was removed.
 */
function dropBaselineRecord(pPict, pContext, pEntity, pGUID)
{
	const tmpStore = getBaselineStore(pPict, false);
	if (!tmpStore || !tmpStore[pContext] || !tmpStore[pContext][pEntity])
	{
		return false;
	}
	if (!Object.prototype.hasOwnProperty.call(tmpStore[pContext][pEntity], pGUID))
	{
		return false;
	}
	delete tmpStore[pContext][pEntity][pGUID];
	return true;
}

/** Where a save's emitted context roots accumulate between the providers and the document PUT. */
const PENDING_EMISSION_MARKER = '_EntityDataPendingEmission';

/**
 * Record a context root this save is going to carry, for the single fold that happens before the PUT.
 *
 * Every provider on the form runs its own sweep and its own prune over the SAME context root, so the
 * fold cannot live in the provider: the first provider to fold would advance the baseline, and the
 * next provider's prune would then find every record unchanged and DELETE it from the root - sending
 * an empty comprehension while the baseline claimed it had been written. The prune is only idempotent
 * across providers while the baseline stays still.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pContext - The comprehension context.
 * @param {Record<string, any>} pContextRoot - The pruned `<Entity>.<GUID>` root.
 * @return {void}
 */
function recordPendingEmission(pPict, pContext, pContextRoot)
{
	if (!pPict || typeof pPict !== 'object')
	{
		return;
	}
	if (!pPict[PENDING_EMISSION_MARKER] || typeof pPict[PENDING_EMISSION_MARKER].set !== 'function')
	{
		pPict[PENDING_EMISSION_MARKER] = new Map();
	}
	pPict[PENDING_EMISSION_MARKER].set(pContext, pContextRoot);
}

/**
 * Fold everything this save is carrying into the baseline, once, and forget it.
 *
 * Called from the document save immediately before the PUT: the baseline lives in `FormData` and
 * `FormData` is snapshotted into the PUT body, so folding afterwards leaves the new baseline in memory
 * only and a reload starts from the stale one.
 *
 * @param {Object} pPict - The pict instance.
 * @return {number} - How many records were folded.
 */
function foldPendingEmission(pPict)
{
	const tmpPending = pPict && pPict[PENDING_EMISSION_MARKER];
	if (!tmpPending || typeof tmpPending.forEach !== 'function')
	{
		return 0;
	}
	const tmpCount = foldEmissionIntoBaseline(pPict, tmpPending);
	delete pPict[PENDING_EMISSION_MARKER];
	return tmpCount;
}

/**
 * The context roots this save is carrying, as `[ context, root ]` pairs. Taken before the fold, which
 * forgets them, so the records the PUT sends can still be worked on after the baseline has copied them.
 *
 * @param {Object} pPict - The pict instance.
 * @return {Array<[string, Record<string, any>]>} - The pending roots.
 */
function getPendingEmissionRoots(pPict)
{
	const tmpPending = pPict && pPict[PENDING_EMISSION_MARKER];
	if (!tmpPending || typeof tmpPending.forEach !== 'function')
	{
		return [ ];
	}
	return Array.from(tmpPending.entries());
}

/**
 * @param {any} pValue
 * @return {boolean} - True for a plain object (not null, not an array).
 */
function isPlainObject(pValue)
{
	return !!pValue && typeof pValue === 'object' && !Array.isArray(pValue);
}

/**
 * Lay a form's copy of a JSON column over the server's copy.
 *
 * A form binds only some keys inside a JSON column, and the server stores the column whole, so the
 * form's object alone would drop every key it never loaded. Keys the form holds win, nested objects
 * merge key by key, arrays and scalars replace. A blank, null or missing form value keeps the server's
 * value: a form field the record never filled in reads as empty, which is not the user clearing it.
 *
 * @param {Record<string, any>} pServerValue - The column as the server holds it.
 * @param {Record<string, any>} pFormValue - The column as the form holds it.
 * @return {Record<string, any>} - A new merged object; neither input is changed.
 */
function mergeObjectOverServer(pServerValue, pFormValue)
{
	const tmpMerged = isPlainObject(pServerValue) ? JSON.parse(JSON.stringify(pServerValue)) : { };
	if (!isPlainObject(pFormValue))
	{
		return tmpMerged;
	}
	for (const tmpKey of Object.keys(pFormValue))
	{
		const tmpValue = pFormValue[tmpKey];
		if (tmpValue === null || typeof tmpValue === 'undefined' || tmpValue === '')
		{
			continue;
		}
		if (isPlainObject(tmpValue))
		{
			tmpMerged[tmpKey] = mergeObjectOverServer(tmpMerged[tmpKey], tmpValue);
			continue;
		}
		tmpMerged[tmpKey] = (typeof tmpValue === 'object') ? JSON.parse(JSON.stringify(tmpValue)) : tmpValue;
	}
	return tmpMerged;
}

/**
 * Read the one server row a held record names, by id when it has one, otherwise by GUID.
 *
 * @param {Object} pPict - The pict instance (owns HeadlightRestClient).
 * @param {string} pEntity - The entity name.
 * @param {Record<string, any>} pRecord - The emitted record.
 * @param {(pError: any, pRow: ?Record<string, any>) => void} fCallback - The row, or null when the server has none.
 */
function readServerRow(pPict, pEntity, pRecord, fCallback)
{
	const tmpID = Number(pRecord[`ID${pEntity}`]);
	if (tmpID > 0)
	{
		return libSharedEntityRead.readJSON(pPict, `${pEntity}/${tmpID}`, { ReuseResult: false }, (pError, pRow) =>
		{
			if (pError)
			{
				return fCallback(pError, null);
			}
			return fCallback(null, (isPlainObject(pRow) && Number(pRow[`ID${pEntity}`]) > 0) ? pRow : null);
		});
	}
	const tmpGUID = pRecord[`GUID${pEntity}`];
	if (libSharedEntityRead.isEmptyIdentityValue(`GUID${pEntity}`, tmpGUID))
	{
		return fCallback(null, null);
	}
	libSharedEntityRead.readJSON(pPict, `${pEntity}s/FilteredTo/FBV~GUID${pEntity}~EQ~${encodeURIComponent(tmpGUID)}/0/2`, { ReuseResult: false }, (pError, pRows) =>
	{
		if (pError)
		{
			return fCallback(pError, null);
		}
		return fCallback(null, (Array.isArray(pRows) && pRows.length === 1) ? pRows[0] : null);
	});
}

/**
 * Before the PUT, merge every JSON-object field the save carries over the server's copy of it, so a
 * form that binds only part of a column cannot drop the rest. Runs after the fold: the baseline keeps
 * the form's own view, which is what the next save's prune compares against.
 *
 * A record the server does not have yet goes as the form built it. When the server copy cannot be
 * read, the object field is left out of this save and the record's baseline dropped, so the next save
 * sends it again rather than either losing the edit or sending a partial column.
 *
 * @param {Object} pPict - The pict instance.
 * @param {Array<[string, Record<string, any>]>} pRoots - The roots `getPendingEmissionRoots` returned.
 * @param {(pCounts: { Merged: number, Withheld: number }) => void} fDone - Called once with what happened.
 */
function mergePendingObjectFieldsWithServer(pPict, pRoots, fDone)
{
	const tmpCounts = { Merged: 0, Withheld: 0 };
	const tmpTasks = [ ];
	for (const [ tmpContext, tmpContextRoot ] of (pRoots || [ ]))
	{
		if (!isPlainObject(tmpContextRoot))
		{
			continue;
		}
		for (const tmpEntity of Object.keys(tmpContextRoot))
		{
			const tmpBucket = tmpContextRoot[tmpEntity];
			if (!isPlainObject(tmpBucket))
			{
				continue;
			}
			for (const tmpGUID of Object.keys(tmpBucket))
			{
				const tmpRecord = tmpBucket[tmpGUID];
				if (!isPlainObject(tmpRecord) || isTombstoneRecord(tmpRecord))
				{
					continue;
				}
				const tmpObjectFields = Object.keys(tmpRecord).filter((pField) => isPlainObject(tmpRecord[pField]));
				if (tmpObjectFields.length < 1)
				{
					continue;
				}
				tmpTasks.push((fNext) =>
				{
					readServerRow(pPict, tmpEntity, tmpRecord, (pError, pRow) =>
					{
						if (pError)
						{
							for (const tmpField of tmpObjectFields)
							{
								delete tmpRecord[tmpField];
							}
							dropBaselineRecord(pPict, tmpContext, tmpEntity, tmpGUID);
							++tmpCounts.Withheld;
							if (pPict && pPict.log && typeof pPict.log.warn === 'function')
							{
								pPict.log.warn(`EntityDataBaseline: could not read ${tmpEntity}/${tmpGUID} to merge ${tmpObjectFields.join(', ')}; left out of this save, sent again on the next.`);
							}
							return fNext();
						}
						if (!pRow)
						{
							return fNext();
						}
						for (const tmpField of tmpObjectFields)
						{
							let tmpServerValue = pRow[tmpField];
							if (typeof tmpServerValue === 'string')
							{
								try
								{
									tmpServerValue = JSON.parse(tmpServerValue);
								}
								catch (pParseError)
								{
									tmpServerValue = null;
								}
							}
							tmpRecord[tmpField] = mergeObjectOverServer(tmpServerValue, tmpRecord[tmpField]);
						}
						++tmpCounts.Merged;
						return fNext();
					});
				});
			}
		}
	}
	runTasksWithConcurrency(tmpTasks, 4, () => fDone(tmpCounts));
}

/**
 * Discard the whole baseline.
 *
 * Used when a save fails. The baseline is folded forward BEFORE the PUT so it rides the same payload
 * as the comprehension it describes; if that PUT does not land, the baseline is claiming records were
 * written that may not have been. Clearing makes the retry project everything whole, which is the safe
 * direction - over-emitting costs a redundant upsert, under-emitting loses the record.
 *
 * @param {Object} pPict - The pict instance.
 * @return {boolean} - True when a baseline was present and has been cleared.
 */
function clearBaseline(pPict)
{
	const tmpStore = getBaselineStore(pPict, false);
	if (!tmpStore)
	{
		return false;
	}
	for (const tmpContext of Object.keys(tmpStore))
	{
		delete tmpStore[tmpContext];
	}
	return true;
}

/**
 * Whether a baseline has been captured at all on this document.
 *
 * Until it has, every record projects whole — which is the pre-delta behaviour, and the correct
 * fallback: a missing baseline must never be read as "nothing changed".
 *
 * @param {Object} pPict - The pict instance.
 * @return {boolean} - True when a baseline exists.
 */
function hasBaseline(pPict)
{
	const tmpStore = getBaselineStore(pPict, false);
	return !!(tmpStore && Object.keys(tmpStore).length > 0);
}

/**
 * Narrow one context root from a state assertion to a change record.
 *
 * The sweep has just keyed every participating input's whole record into `pContextRoot`. This is the
 * one place that decides what actually ships, and it is the right place because it already walks
 * every projected leaf and because the decision needs the record, not the writer — sibling inputs
 * write into each other's records constantly, so dirtiness is a property of the fields, not of the
 * owning input's own events.
 *
 * Per leaf: sanitize as before, then diff against the baseline the settled load sweep captured.
 * Unchanged records are removed from the tree entirely, which is what makes an untouched save emit
 * an empty comprehension. Records with no baseline — ones this form created — project whole.
 * Tombstones pass through untouched; a delete is a minted event, not an inferred one.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pContext - The context this root hangs under.
 * @param {Record<string, any>} pContextRoot - The context root to narrow in place.
 * @return {{ Emitted: number, Suppressed: number }} - How many records shipped and how many were dropped.
 */
function pruneContextRootToDelta(pPict, pContext, pContextRoot)
{
	const tmpCounts = { Emitted: 0, Suppressed: 0 };
	const tmpEmittedDetail = [];
	if (!pContextRoot || typeof pContextRoot !== 'object' || Array.isArray(pContextRoot))
	{
		if (pPict && pPict.log && typeof pPict.log.trace === 'function')
		{
		pPict.log.trace(`EntityDataBaseline: ${pContext} emitting ${tmpCounts.Emitted}, suppressed ${tmpCounts.Suppressed} — ${tmpEmittedDetail.join(', ') || 'nothing'}.`);
		}
		return tmpCounts;
	}
	for (const tmpEntity of Object.keys(pContextRoot))
	{
		const tmpBucket = pContextRoot[tmpEntity];
		if (!tmpBucket || typeof tmpBucket !== 'object' || Array.isArray(tmpBucket))
		{
			continue;
		}
		for (const tmpGUID of Object.keys(tmpBucket))
		{
			const tmpRecord = tmpBucket[tmpGUID];
			if (!tmpRecord || typeof tmpRecord !== 'object' || Array.isArray(tmpRecord))
			{
				continue;
			}
			const tmpSanitized = libScopedEntityData.sanitizeRecord(tmpRecord);
			const tmpUnemittableReason = libScopedEntityData.getUnemittableReason(tmpEntity, tmpGUID, tmpSanitized);
			if (tmpUnemittableReason)
			{
				delete tmpBucket[tmpGUID];
				const tmpMessage = `EntityDataBaseline: dropped ${pContext}.${tmpEntity}.${tmpGUID} from the save because ${tmpUnemittableReason}.`;
				if (pPict && pPict.log && typeof pPict.log.warn === 'function')
				{
					pPict.log.warn(tmpMessage);
				}
				else
				{
					console.warn(tmpMessage);
				}
				continue;
			}
			const tmpBaseline = readBaselineRecord(pPict, pContext, tmpEntity, tmpGUID);
			const tmpDelta = computeRecordDelta(tmpEntity, tmpBaseline, tmpSanitized);
			if (!tmpDelta)
			{
				delete tmpBucket[tmpGUID];
				++tmpCounts.Suppressed;
				continue;
			}
			tmpEmittedDetail.push(`${tmpEntity}${tmpBaseline ? '(changed)' : '(NO BASELINE)'}`);
			tmpBucket[tmpGUID] = tmpDelta;
			++tmpCounts.Emitted;
		}
		if (Object.keys(tmpBucket).length < 1)
		{
			delete pContextRoot[tmpEntity];
		}
	}
	return tmpCounts;
}

/**
 * The sweep event both input families answer by projecting their records. The address half is
 * legacy-ledger vestige on the scoped path — the projectors resolve their own destination — but the
 * event string is matched whole by several subclasses, so it is reproduced verbatim.
 */
const PROJECTION_SWEEP_EVENT = 'ProjectEntityDataRecords:AppData.DocumentData.ReportData.FormData._EntityData';

/** The AppData key the baseline's session state lives under: beside `DocumentData`, so the document save never carries it. */
const APP_STATE_KEY = 'EntityDataBaseline';

/**
 * The baseline's session state: the capture session, and the render transactions a load-settled
 * capture is already scheduled on (so N inputs schedule one).
 *
 * @param {Object} pPict - The pict instance.
 * @return {{ CaptureSession: ?{ Store: Record<string, any>, Known: Set<string> }, ScheduledTransactions: Record<string, boolean> }|null} - The state, or null without a pict.
 */
function getBaselineAppState(pPict)
{
	if (!pPict || typeof pPict !== 'object')
	{
		return null;
	}
	if (!pPict.AppData || typeof pPict.AppData !== 'object')
	{
		pPict.AppData = { };
	}
	const tmpState = pPict.AppData[APP_STATE_KEY];
	if (!tmpState || typeof tmpState !== 'object')
	{
		pPict.AppData[APP_STATE_KEY] = { CaptureSession: null, ScheduledTransactions: { } };
	}
	return pPict.AppData[APP_STATE_KEY];
}

/**
 * Run the save-time projection sweep into a scratch tree and hand back what it collected, without
 * touching the document's own EntityData.
 *
 * @param {Object} pPict - The pict instance.
 * @return {Map<string, Record<string, any>>|null} - The `<Entity>.<GUID>` roots by context, or null when the sweep could not run.
 */
function runProjectionSweepToScratch(pPict)
{
	const tmpMetacontroller = pPict && pPict.views && pPict.views.PictFormMetacontroller;
	if (!tmpMetacontroller || typeof tmpMetacontroller.triggerGlobalInputEvent !== 'function')
	{
		return null;
	}
	let tmpContextRoots = null;
	libScopedEntityData.beginBaselineSweep(pPict);
	try
	{
		const tmpTransactionGUID = (typeof pPict.getUUID === 'function') ? pPict.getUUID() : `baseline-${Date.now()}`;
		if (pPict.TransactionTracking && typeof pPict.TransactionTracking.registerTransaction === 'function')
		{
			pPict.TransactionTracking.registerTransaction(tmpTransactionGUID);
		}
		tmpMetacontroller.triggerGlobalInputEvent(PROJECTION_SWEEP_EVENT, tmpTransactionGUID);
		if (typeof tmpMetacontroller.finalizeTransaction === 'function')
		{
			tmpMetacontroller.finalizeTransaction(tmpTransactionGUID);
		}
	}
	finally
	{
		// The sweep is synchronous for every projector in the family: each reads records already in
		// FormData and keys them into the target. Ending the redirect in `finally` guarantees the
		// document's own EntityData is never left shadowed if one of them throws.
		tmpContextRoots = libScopedEntityData.endBaselineSweep(pPict);
	}
	return tmpContextRoots || null;
}

/** How deep the identity writeback will walk FormData before giving up. */
const IDENTITY_WALK_MAX_DEPTH = 14;

/**
 * Write the row ids the server resolved back onto the records this form holds.
 *
 * The client mints GUIDs and the server resolves them to rows, so until the ids come back the form
 * has no way to know its records exist - which is what `hasServerIdentity` asks before a record may
 * seed a baseline. Without this the gate can never pass for a record the form minted and nothing
 * re-fetches, so it re-emits whole on every save forever.
 *
 * Walks `FormData` rather than re-running the projection sweep: the sweep is an event round-trip
 * through the metacontroller, and this runs inside the save lifecycle where a nested transaction is
 * not guaranteed to resolve. A record is matched on its own `GUID<Entity>`, so nesting and array
 * position do not matter, and an id already present is left alone.
 *
 * @param {Object} pPict - The pict instance.
 * @param {Record<string, Record<string, number>>} pIdentities - Entity -> external GUID -> row id.
 * @return {number} - How many records learned their id.
 */
function applyResolvedIdentities(pPict, pIdentities)
{
	if (!pIdentities || typeof pIdentities !== 'object' || Array.isArray(pIdentities))
	{
		return 0;
	}
	const tmpEntities = Object.keys(pIdentities);
	if (tmpEntities.length < 1)
	{
		return 0;
	}
	if (!pPict || !pPict.manifest || typeof pPict.manifest.getValueAtAddress !== 'function')
	{
		return 0;
	}
	const tmpFormData = pPict.manifest.getValueAtAddress(pPict, FORMDATA_ADDRESS);
	if (!tmpFormData || typeof tmpFormData !== 'object')
	{
		return 0;
	}

	let tmpCount = 0;
	const tmpSeen = new Set();
	const tmpStack = [ { Node: tmpFormData, Depth: 0 } ];
	while (tmpStack.length > 0)
	{
		const tmpFrame = tmpStack.pop();
		const tmpNode = tmpFrame.Node;
		if (!tmpNode || typeof tmpNode !== 'object' || tmpSeen.has(tmpNode) || tmpFrame.Depth > IDENTITY_WALK_MAX_DEPTH)
		{
			continue;
		}
		tmpSeen.add(tmpNode);

		if (!Array.isArray(tmpNode))
		{
			// A record carries a GUID for itself AND for everything it references - a DocumentPolyJoin
			// holds both `GUIDDocumentPolyJoin` and `GUIDSample`. Its own entity is the most specific
			// name it matches, because a join's name contains the names of what it joins; writing the
			// others would put a foreign id on the record and emit it as a column the table lacks.
			let tmpOwnEntity = null;
			let tmpOwnIdentity = 0;
			for (const tmpEntity of tmpEntities)
			{
				const tmpGUID = tmpNode[`GUID${tmpEntity}`];
				if (typeof tmpGUID !== 'string' || tmpGUID.length < 1)
				{
					continue;
				}
				const tmpIdentity = Number(pIdentities[tmpEntity][tmpGUID]);
				if (!(tmpIdentity > 0))
				{
					continue;
				}
				if (!tmpOwnEntity || tmpEntity.length > tmpOwnEntity.length)
				{
					tmpOwnEntity = tmpEntity;
					tmpOwnIdentity = tmpIdentity;
				}
			}
			if (tmpOwnEntity && !hasServerIdentity(tmpOwnEntity, tmpNode))
			{
				tmpNode[`ID${tmpOwnEntity}`] = tmpOwnIdentity;
				++tmpCount;
			}
		}

		for (const tmpKey of Object.keys(tmpNode))
		{
			// The baseline is a record store of its own; rewriting its leaves would change what the
			// next emission diffs against.
			if (tmpKey === BASELINE_KEY)
			{
				continue;
			}
			const tmpChild = tmpNode[tmpKey];
			if (tmpChild && typeof tmpChild === 'object')
			{
				tmpStack.push({ Node: tmpChild, Depth: tmpFrame.Depth + 1 });
			}
		}
	}
	return tmpCount;
}

/** How many held-identity reads are in flight at once. */
const HELD_IDENTITY_READ_CONCURRENCY = 4;

/** Keys a held-record walk never descends into: the legacy ledger and the baseline store. */
const HELD_RECORD_SKIPPED_KEYS = new Set([ '_EntityData', BASELINE_KEY ]);

/** The join whose table's unique key (document, remote type, remote row) makes a natural-key match unambiguous. */
const DOCUMENT_JOIN_ENTITY = 'DocumentPolyJoin';

/** Remote types a held document join may be matched to a live join by. */
const DOCUMENT_JOIN_NATURAL_KEY_TYPES = new Set([ 'Sample', 'TestInstance' ]);

/** The join whose table's unique key (TestInstance, Lab) makes a natural-key match unambiguous. */
const LAB_JOIN_ENTITY = 'TestInstanceLabJoin';

/**
 * Whether the document is frozen: rendered read-only, or locked. A frozen document keeps its saved
 * snapshot, so nothing may refresh or rewrite the records it holds.
 *
 * @param {Object} pPict - The pict instance.
 * @return {boolean} - True when the document is not interactive or is locked.
 */
function isDocumentFrozen(pPict)
{
	const tmpAppData = pPict && pPict.AppData;
	if (!tmpAppData || !tmpAppData.Interactive)
	{
		return true;
	}
	const tmpDocument = tmpAppData.DocumentData && tmpAppData.DocumentData.ReportData;
	return !!(tmpDocument && tmpDocument.Locked);
}

/**
 * Whether saves go to the device's local store rather than the server, so server reads do not apply.
 *
 * @param {Object} pPict - The pict instance.
 * @return {boolean} - True on the local offline transport.
 */
function isLocalDocumentTransport(pPict)
{
	const tmpService = pPict && pPict.services && pPict.services.HeadlightDocumentService;
	return !!(tmpService && typeof tmpService.isLocalDocumentTransport === 'function' && tmpService.isLocalDocumentTransport());
}

/**
 * Every entity record the form holds in FormData, outside the legacy ledger and the baseline store,
 * each tagged with the entity it names itself as.
 *
 * @param {Object} pPict - The pict instance.
 * @return {Array<{ Entity: string, Record: Record<string, any> }>} - The held records.
 */
function collectHeldRecords(pPict)
{
	const tmpHeld = [ ];
	const tmpFormData = pPict.manifest.getValueAtAddress(pPict, FORMDATA_ADDRESS);
	const tmpSeen = new Set();
	const tmpStack = [ { Node: tmpFormData, Depth: 0 } ];
	while (tmpStack.length > 0)
	{
		const tmpFrame = tmpStack.pop();
		const tmpNode = tmpFrame.Node;
		if (!tmpNode || typeof tmpNode !== 'object' || tmpSeen.has(tmpNode) || tmpFrame.Depth > IDENTITY_WALK_MAX_DEPTH)
		{
			continue;
		}
		tmpSeen.add(tmpNode);
		if (!Array.isArray(tmpNode))
		{
			const tmpEntity = libScopedEntityData.resolveRecordEntity(tmpNode);
			if (tmpEntity)
			{
				tmpHeld.push({ Entity: tmpEntity, Record: tmpNode });
			}
		}
		for (const tmpKey of Object.keys(tmpNode))
		{
			if (HELD_RECORD_SKIPPED_KEYS.has(tmpKey))
			{
				continue;
			}
			const tmpChild = tmpNode[tmpKey];
			if (tmpChild && typeof tmpChild === 'object')
			{
				tmpStack.push({ Node: tmpChild, Depth: tmpFrame.Depth + 1 });
			}
		}
	}
	return tmpHeld;
}

/**
 * Run callback-style tasks with at most `pLimit` in flight, then call `fDone` exactly once.
 *
 * @param {Array<(fNext: () => void) => void>} pTasks - The tasks.
 * @param {number} pLimit - The concurrency cap.
 * @param {() => void} fDone - Called once every task has settled.
 */
function runTasksWithConcurrency(pTasks, pLimit, fDone)
{
	if (pTasks.length < 1)
	{
		return fDone();
	}
	let tmpNext = 0;
	let tmpFinished = 0;
	const fLaunch = () =>
	{
		if (tmpNext >= pTasks.length)
		{
			return;
		}
		const tmpTask = pTasks[tmpNext++];
		let tmpSettled = false;
		const fSettle = () =>
		{
			if (tmpSettled)
			{
				return;
			}
			tmpSettled = true;
			tmpFinished++;
			if (tmpFinished === pTasks.length)
			{
				return fDone();
			}
			fLaunch();
		};
		try
		{
			tmpTask(fSettle);
		}
		catch (pError)
		{
			fSettle();
		}
	};
	for (let i = 0; i < Math.min(pLimit, pTasks.length); i++)
	{
		fLaunch();
	}
}

/**
 * The id of the one live row carrying a GUID, or 0 when the read failed, found nothing, or was ambiguous.
 * Render transactions settle together, so identical reads share one request (`_SharedEntityRead`).
 *
 * @param {Object} pPict - The pict instance (owns HeadlightRestClient).
 * @param {string} pEntity - The entity name.
 * @param {string} pGUID - The external GUID.
 * @param {(pID: number) => void} fCallback - Called once with the id or 0.
 */
function readLiveRowID(pPict, pEntity, pGUID, fCallback)
{
	if (libSharedEntityRead.isEmptyIdentityValue(`GUID${pEntity}`, pGUID))
	{
		return fCallback(0);
	}
	libSharedEntityRead.readJSON(pPict, `${pEntity}s/FilteredTo/FBV~GUID${pEntity}~EQ~${encodeURIComponent(pGUID)}/0/2`, { ReuseResult: false }, (pError, pRows) =>
	{
		if (pError || !Array.isArray(pRows) || pRows.length !== 1)
		{
			return fCallback(0);
		}
		const tmpID = Number(pRows[0] && pRows[0][`ID${pEntity}`]);
		return fCallback(tmpID > 0 ? tmpID : 0);
	});
}

/**
 * Stamp resolved row ids onto the held records they belong to, matching each record on its OWN entity.
 *
 * A join carries the GUIDs of what it joins beside its own, so matching on any `GUID<X>` would put a
 * foreign id on it; `resolveRecordEntity` names the one entity the record is.
 *
 * @param {Object} pPict - The pict instance.
 * @param {Record<string, Record<string, number>>} pResolved - Entity -> GUID -> row id.
 * @return {number} - How many records learned their id.
 */
function stampHeldIdentities(pPict, pResolved)
{
	let tmpCount = 0;
	for (const tmpItem of collectHeldRecords(pPict))
	{
		const tmpBucket = pResolved[tmpItem.Entity];
		const tmpGUID = tmpItem.Record[`GUID${tmpItem.Entity}`];
		if (!tmpBucket || typeof tmpGUID !== 'string' || !(tmpBucket[tmpGUID] > 0) || hasServerIdentity(tmpItem.Entity, tmpItem.Record))
		{
			continue;
		}
		tmpItem.Record[`ID${tmpItem.Entity}`] = tmpBucket[tmpGUID];
		++tmpCount;
	}
	return tmpCount;
}

/**
 * Point every held record carrying `pFromGUID` at a different row: its GUID and its id.
 *
 * @param {Object} pPict - The pict instance.
 * @param {string} pEntity - The entity name.
 * @param {string} pFromGUID - The GUID the held records carry now.
 * @param {string} pToGUID - The live row's GUID.
 * @param {number} pToID - The live row's id.
 * @return {number} - How many held records were rewritten.
 */
function rewriteHeldRecordIdentity(pPict, pEntity, pFromGUID, pToGUID, pToID)
{
	const tmpGUIDField = `GUID${pEntity}`;
	let tmpCount = 0;
	for (const tmpItem of collectHeldRecords(pPict))
	{
		if (tmpItem.Entity === pEntity && tmpItem.Record[tmpGUIDField] === pFromGUID)
		{
			tmpItem.Record[tmpGUIDField] = pToGUID;
			tmpItem.Record[`ID${pEntity}`] = pToID;
			++tmpCount;
		}
	}
	return tmpCount;
}

/**
 * Point held document joins whose own GUID names no row at the live join for the same document,
 * remote type and remote row. That triple is the table's unique key, so the match is the only one
 * possible; a remote row the form cannot resolve to an id, or a live join another held record already
 * carries, is left alone.
 *
 * @param {Object} pPict - The pict instance.
 * @param {Object} pRestClient - The REST client.
 * @param {Array<Record<string, any>>} pUnresolved - Held document joins with no live row under their GUID.
 * @param {Record<string, Record<string, number>>} pKnownIdentities - Entity -> GUID -> row id, to resolve a join's remote row.
 * @param {(pAdopted: number) => void} fCallback - Called once with how many joins were adopted.
 */
function adoptDocumentJoinsByNaturalKey(pPict, pRestClient, pUnresolved, pKnownIdentities, fCallback)
{
	const tmpDocument = pPict.AppData && pPict.AppData.DocumentData && pPict.AppData.DocumentData.ReportData;
	const tmpIDDocument = Number(tmpDocument && tmpDocument.IDDocument);
	const tmpCandidates = [ ];
	for (const tmpRecord of pUnresolved)
	{
		if (!tmpRecord || !DOCUMENT_JOIN_NATURAL_KEY_TYPES.has(tmpRecord.RemoteType))
		{
			continue;
		}
		let tmpIDRemote = Number(tmpRecord.IDRemote);
		if (!(tmpIDRemote > 0))
		{
			const tmpRemoteGUID = tmpRecord[`GUID${tmpRecord.RemoteType}`];
			const tmpKnown = pKnownIdentities[tmpRecord.RemoteType];
			tmpIDRemote = (tmpKnown && typeof tmpRemoteGUID === 'string') ? Number(tmpKnown[tmpRemoteGUID]) : 0;
		}
		if (tmpIDRemote > 0)
		{
			tmpCandidates.push({ GUID: tmpRecord[`GUID${DOCUMENT_JOIN_ENTITY}`], Key: `${tmpRecord.RemoteType}:${tmpIDRemote}` });
		}
	}
	if (!(tmpIDDocument > 0) || tmpCandidates.length < 1)
	{
		return fCallback(0);
	}
	const fSafeCallback = (pAdopted) =>
	{
		fCallback(pAdopted);
	};
	try
	{
		pRestClient.getJSON(`${DOCUMENT_JOIN_ENTITY}s/FilteredTo/FBV~IDDocument~EQ~${tmpIDDocument}/0/500`, (pError, pRows) =>
		{
			let tmpAdopted = 0;
			try
			{
				if (pError || !Array.isArray(pRows))
				{
					return;
				}
				const tmpLiveByKey = new Map();
				for (const tmpRow of pRows)
				{
					if (!tmpRow || !(Number(tmpRow.IDDocumentPolyJoin) > 0) || typeof tmpRow.GUIDDocumentPolyJoin !== 'string')
					{
						continue;
					}
					const tmpKey = `${tmpRow.RemoteType}:${Number(tmpRow.IDRemote)}`;
					tmpLiveByKey.set(tmpKey, tmpLiveByKey.has(tmpKey) ? null : tmpRow);
				}
				const tmpHeldGUIDs = new Set(collectHeldRecords(pPict)
					.filter((pItem) => pItem.Entity === DOCUMENT_JOIN_ENTITY)
					.map((pItem) => pItem.Record[`GUID${DOCUMENT_JOIN_ENTITY}`]));
				for (const tmpCandidate of tmpCandidates)
				{
					const tmpRow = tmpLiveByKey.get(tmpCandidate.Key);
					if (!tmpRow || tmpHeldGUIDs.has(tmpRow.GUIDDocumentPolyJoin))
					{
						continue;
					}
					tmpHeldGUIDs.add(tmpRow.GUIDDocumentPolyJoin);
					if (rewriteHeldRecordIdentity(pPict, DOCUMENT_JOIN_ENTITY, tmpCandidate.GUID, tmpRow.GUIDDocumentPolyJoin, Number(tmpRow.IDDocumentPolyJoin)) > 0)
					{
						++tmpAdopted;
					}
				}
			}
			finally
			{
				fSafeCallback(tmpAdopted);
			}
		});
	}
	catch (pError)
	{
		fSafeCallback(0);
	}
}

/**
 * Point held TestInstance lab joins whose own GUID names no row at the live lab join for the same
 * TestInstance and Lab. That pair is the table's unique key, so the match is the only one possible; a
 * TestInstance the form cannot resolve to an id, a live join another held record already carries, or a
 * pair with more than one live row is left alone.
 *
 * @param {Object} pPict - The pict instance.
 * @param {Object} pRestClient - The REST client.
 * @param {Array<Record<string, any>>} pUnresolved - Held lab joins with no live row under their GUID.
 * @param {Record<string, Record<string, number>>} pKnownIdentities - Entity -> GUID -> row id, to resolve a join's TestInstance.
 * @param {(pAdopted: number) => void} fCallback - Called once with how many lab joins were adopted.
 */
function adoptLabJoinsByNaturalKey(pPict, pRestClient, pUnresolved, pKnownIdentities, fCallback)
{
	const tmpKnownTestInstances = pKnownIdentities.TestInstance || { };
	const tmpCandidates = [ ];
	for (const tmpRecord of pUnresolved)
	{
		if (!tmpRecord)
		{
			continue;
		}
		let tmpIDTestInstance = Number(tmpRecord.IDTestInstance);
		if (!(tmpIDTestInstance > 0) && typeof tmpRecord.GUIDTestInstance === 'string')
		{
			tmpIDTestInstance = Number(tmpKnownTestInstances[tmpRecord.GUIDTestInstance]);
		}
		const tmpIDLab = Number(tmpRecord.IDLab);
		if (tmpIDTestInstance > 0 && tmpIDLab > 0)
		{
			tmpCandidates.push({ GUID: tmpRecord[`GUID${LAB_JOIN_ENTITY}`], Key: `${tmpIDTestInstance}:${tmpIDLab}`, IDTestInstance: tmpIDTestInstance });
		}
	}
	if (tmpCandidates.length < 1)
	{
		return fCallback(0);
	}
	let tmpCalled = false;
	const fSafeCallback = (pAdopted) =>
	{
		if (tmpCalled)
		{
			return;
		}
		tmpCalled = true;
		fCallback(pAdopted);
	};
	const tmpIDTestInstances = [ ...new Set(tmpCandidates.map((pCandidate) => pCandidate.IDTestInstance)) ];
	try
	{
		pRestClient.getJSON(`${LAB_JOIN_ENTITY}s/FilteredTo/FBL~IDTestInstance~INN~${tmpIDTestInstances.join(',')}/0/500`, (pError, pRows) =>
		{
			let tmpAdopted = 0;
			try
			{
				if (pError || !Array.isArray(pRows))
				{
					return;
				}
				const tmpLiveByKey = new Map();
				for (const tmpRow of pRows)
				{
					if (!tmpRow || !(Number(tmpRow.IDTestInstanceLabJoin) > 0) || typeof tmpRow.GUIDTestInstanceLabJoin !== 'string')
					{
						continue;
					}
					const tmpKey = `${Number(tmpRow.IDTestInstance)}:${Number(tmpRow.IDLab)}`;
					tmpLiveByKey.set(tmpKey, tmpLiveByKey.has(tmpKey) ? null : tmpRow);
				}
				const tmpHeldGUIDs = new Set(collectHeldRecords(pPict)
					.filter((pItem) => pItem.Entity === LAB_JOIN_ENTITY)
					.map((pItem) => pItem.Record[`GUID${LAB_JOIN_ENTITY}`]));
				for (const tmpCandidate of tmpCandidates)
				{
					const tmpRow = tmpLiveByKey.get(tmpCandidate.Key);
					if (!tmpRow || tmpHeldGUIDs.has(tmpRow.GUIDTestInstanceLabJoin))
					{
						continue;
					}
					tmpHeldGUIDs.add(tmpRow.GUIDTestInstanceLabJoin);
					if (rewriteHeldRecordIdentity(pPict, LAB_JOIN_ENTITY, tmpCandidate.GUID, tmpRow.GUIDTestInstanceLabJoin, Number(tmpRow.IDTestInstanceLabJoin)) > 0)
					{
						++tmpAdopted;
					}
				}
			}
			finally
			{
				fSafeCallback(tmpAdopted);
			}
		});
	}
	catch (pError)
	{
		fSafeCallback(0);
	}
}

/**
 * Resolve the row ids of records the form holds without one, before the load baseline is captured.
 *
 * A record an earlier form version saved, or whose id never came back, carries only its GUID, so the
 * delta has no baseline for it and the first save re-sends it whole. Reading each such GUID's live row
 * and stamping the id makes it baselined like any other record. A held document join or TestInstance
 * lab join whose own GUID names no row is pointed at the live row for the same natural key. Only live
 * rows are adopted: a failed read, a missing row or a deleted one changes nothing.
 *
 * Meant to run once the load has settled, so it sees every record the loaders created or replaced.
 * Calls `fDone` exactly once: after every read has returned, or at once when there is nothing to read.
 * Skipped for a frozen document, on the local offline transport, and without a REST client.
 *
 * @param {Object} pPict - The pict instance.
 * @param {() => void} [fDone] - Called once, when the held records are settled.
 * @return {boolean} - True when reads were started.
 */
function resolveHeldIdentities(pPict, fDone)
{
	let tmpFinished = false;
	const fFinish = (pMessage) =>
	{
		if (tmpFinished)
		{
			return;
		}
		tmpFinished = true;
		if (pMessage && pPict && pPict.log && typeof pPict.log.info === 'function')
		{
			pPict.log.info(pMessage);
		}
		if (typeof fDone === 'function')
		{
			fDone();
		}
	};
	if (!pPict || !pPict.manifest || typeof pPict.manifest.getValueAtAddress !== 'function' || isDocumentFrozen(pPict) || isLocalDocumentTransport(pPict))
	{
		fFinish(null);
		return false;
	}
	const tmpRestClient = pPict.HeadlightRestClient;
	if (!tmpRestClient || typeof tmpRestClient.getJSON !== 'function')
	{
		fFinish(null);
		return false;
	}

	const tmpKnown = { };
	const tmpPending = new Map();
	for (const tmpItem of collectHeldRecords(pPict))
	{
		const tmpGUID = tmpItem.Record[`GUID${tmpItem.Entity}`];
		if (typeof tmpGUID !== 'string' || tmpGUID.length < 1)
		{
			continue;
		}
		if (hasServerIdentity(tmpItem.Entity, tmpItem.Record))
		{
			if (!tmpKnown[tmpItem.Entity])
			{
				tmpKnown[tmpItem.Entity] = { };
			}
			tmpKnown[tmpItem.Entity][tmpGUID] = Number(tmpItem.Record[`ID${tmpItem.Entity}`]);
			continue;
		}
		const tmpKey = `${tmpItem.Entity} ${tmpGUID}`;
		if (!tmpPending.has(tmpKey))
		{
			tmpPending.set(tmpKey, { Entity: tmpItem.Entity, GUID: tmpGUID, Records: [ ] });
		}
		tmpPending.get(tmpKey).Records.push(tmpItem.Record);
	}
	if (tmpPending.size < 1)
	{
		fFinish(null);
		return false;
	}

	/** @type {Record<string, Record<string, number>>} */
	const tmpResolved = { };
	const tmpUnresolvedJoins = [ ];
	const tmpUnresolvedLabJoins = [ ];
	const tmpTasks = Array.from(tmpPending.values()).map((pItem) => (fNext) =>
	{
		readLiveRowID(pPict, pItem.Entity, pItem.GUID, (pID) =>
		{
			if (pID > 0)
			{
				if (!tmpResolved[pItem.Entity])
				{
					tmpResolved[pItem.Entity] = { };
				}
				tmpResolved[pItem.Entity][pItem.GUID] = pID;
			}
			else if (pItem.Entity === DOCUMENT_JOIN_ENTITY)
			{
				tmpUnresolvedJoins.push(...pItem.Records);
			}
			else if (pItem.Entity === LAB_JOIN_ENTITY)
			{
				tmpUnresolvedLabJoins.push(...pItem.Records);
			}
			fNext();
		});
	});
	try
	{
		runTasksWithConcurrency(tmpTasks, HELD_IDENTITY_READ_CONCURRENCY, () =>
		{
			try
			{
				const tmpStamped = stampHeldIdentities(pPict, tmpResolved);
				/** @type {Record<string, Record<string, number>>} */
				const tmpKnownIdentities = { };
				for (const tmpSource of [ tmpKnown, tmpResolved ])
				{
					for (const tmpEntity of Object.keys(tmpSource))
					{
						tmpKnownIdentities[tmpEntity] = Object.assign(tmpKnownIdentities[tmpEntity] || { }, tmpSource[tmpEntity]);
					}
				}
				adoptDocumentJoinsByNaturalKey(pPict, tmpRestClient, tmpUnresolvedJoins, tmpKnownIdentities, (pAdoptedJoins) =>
				{
					try
					{
						adoptLabJoinsByNaturalKey(pPict, tmpRestClient, tmpUnresolvedLabJoins, tmpKnownIdentities, (pAdoptedLabJoins) =>
						{
							const tmpTotal = tmpStamped + pAdoptedJoins + pAdoptedLabJoins;
							fFinish(tmpTotal > 0 ? `EntityDataBaseline: adopted the row ids of ${tmpStamped} held record(s), ${pAdoptedJoins} document join(s) and ${pAdoptedLabJoins} lab join(s) before the load baseline.` : null);
						});
					}
					catch (pError)
					{
						fFinish(`EntityDataBaseline: held-identity adoption stopped early, records keep what they held: ${pError.message || pError}`);
					}
				});
			}
			catch (pError)
			{
				fFinish(`EntityDataBaseline: held-identity adoption stopped early, records keep what they held: ${pError.message || pError}`);
			}
		});
	}
	catch (pError)
	{
		fFinish(`EntityDataBaseline: held-identity reads could not start: ${pError.message || pError}`);
	}
	return true;
}

/**
 * Capture the baseline now: sweep, sanitize, store.
 *
 * @param {Object} pPict - The pict instance.
 * @return {number} - How many records were baselined.
 */
function captureBaselineNow(pPict)
{
	const tmpContextRoots = runProjectionSweepToScratch(pPict);
	if (!tmpContextRoots)
	{
		return 0;
	}
	tmpContextRoots.forEach((pContextRoot) =>
	{
		libScopedEntityData.sanitizeContextRoot(pContextRoot);
	});
	return captureBaselineFromSweep(pPict, tmpContextRoots);
}

/**
 * Schedule the load-settled baseline capture on a render transaction, once however many inputs ask.
 *
 * The render transaction is the settle signal: `onAfterRender` opens it, every record fetch in both
 * families fences on it (`registerEventTransactionAsyncOperation`), and its completion callback runs
 * only once all of them have drained. Capturing there rather than at init is what makes the baseline
 * current server state — `PictForm.RefreshOnOpen` defaults to true, so a form open re-fetches and
 * replaces `__Record` wholesale, and a baseline taken before that lands would read every field
 * another actor changed since the last save as a user edit. The held-identity reads
 * (`resolveHeldIdentities`) start in that callback, so they see every record the loaders created or
 * replaced, and the capture waits for them.
 *
 * @param {Object} pPict - The pict instance.
 * @param {Object} pView - The dynamic-form view (owns the transaction API).
 * @param {string} pTransactionGUID - The render transaction.
 * @return {boolean} - True when this call scheduled the capture.
 */
function scheduleLoadBaselineCapture(pPict, pView, pTransactionGUID)
{
	if (!pPict || !pView || !pTransactionGUID || typeof pView.registerOnTransactionCompleteCallback !== 'function')
	{
		return false;
	}
	const tmpScheduled = getBaselineAppState(pPict).ScheduledTransactions;
	if (tmpScheduled[pTransactionGUID])
	{
		return false;
	}
	tmpScheduled[pTransactionGUID] = true;
	pView.registerOnTransactionCompleteCallback(pTransactionGUID, () =>
	{
		delete tmpScheduled[pTransactionGUID];
		let tmpCaptured = false;
		const fCaptureAndRelease = () =>
		{
			if (tmpCaptured)
			{
				return;
			}
			tmpCaptured = true;
			try
			{
				const tmpFirstCapture = !hasCaptureSession(pPict);
				const tmpCount = captureBaselineNow(pPict);
				if (pPict.log && typeof pPict.log.trace === 'function')
				{
					pPict.log.trace(tmpFirstCapture ?
						`EntityDataBaseline: captured ${tmpCount} record(s) once the load settled.` :
						`EntityDataBaseline: captured ${tmpCount} record(s) first seen on a later render; records already baselined keep their values.`);
				}
			}
			catch (pError)
			{
				if (pPict.log && typeof pPict.log.error === 'function')
				{
					pPict.log.error(`EntityDataBaseline: load capture failed, records will project whole this session: ${pError.message || pError}`);
				}
			}
			try
			{
				releaseUnappliedRecords(pPict, pPict.manifest.getValueAtAddress(pPict, libScopedEntityData.DOCUMENT_ENTITY_DATA_ADDRESS));
			}
			catch (pError)
			{
				if (pPict.log && typeof pPict.log.error === 'function')
				{
					pPict.log.error(`EntityDataBaseline: could not read the server's unapplied records on load: ${pError.message || pError}`);
				}
			}
		};
		try
		{
			resolveHeldIdentities(pPict, fCaptureAndRelease);
		}
		catch (pError)
		{
			if (pPict.log && typeof pPict.log.error === 'function')
			{
				pPict.log.error(`EntityDataBaseline: could not start the held-identity reads: ${pError.message || pError}`);
			}
			fCaptureAndRelease();
		}
	});
	return true;
}

/**
 * The `<Entity>.<GUID>` keys of every record a log entry carried, and whether this entry failed each one.
 *
 * An error naming an entity but no record failed every record of that entity in the entry; an error naming
 * neither failed the whole entry, as a push-order abort does.
 *
 * @param {Record<string, any>} pEntry - One `ExecutionLog` entry.
 * @return {Map<string, { Entity: string, GUID: string, Failed: boolean }>} - Per-record outcome, keyed by entity and GUID.
 */
function readLogEntryOutcomes(pEntry)
{
	const tmpOutcomes = new Map();
	const tmpErrors = Array.isArray(pEntry.Errors) ? pEntry.Errors : [ ];
	const tmpFailedRecords = new Set();
	const tmpFailedEntities = new Set();
	let tmpAllFailed = false;
	for (const tmpError of tmpErrors)
	{
		if (!tmpError || typeof tmpError !== 'object')
		{
			continue;
		}
		if (tmpError.Entity && tmpError.GUID)
		{
			tmpFailedRecords.add(`${tmpError.Entity} ${tmpError.GUID}`);
		}
		else if (tmpError.Entity)
		{
			tmpFailedEntities.add(tmpError.Entity);
		}
		else
		{
			tmpAllFailed = true;
		}
	}
	const tmpComprehension = (pEntry.Comprehension && typeof pEntry.Comprehension === 'object') ? pEntry.Comprehension : { };
	for (const tmpEntity of Object.keys(tmpComprehension))
	{
		const tmpBucket = tmpComprehension[tmpEntity];
		if (!tmpBucket || typeof tmpBucket !== 'object')
		{
			continue;
		}
		const tmpGUIDs = Array.isArray(tmpBucket)
			? tmpBucket.map((pRecord) => pRecord && pRecord[`GUID${tmpEntity}`]).filter((pGUID) => !!pGUID)
			: Object.keys(tmpBucket);
		for (const tmpGUID of tmpGUIDs)
		{
			const tmpKey = `${tmpEntity} ${tmpGUID}`;
			const tmpFailed = tmpAllFailed || tmpFailedEntities.has(tmpEntity) || tmpFailedRecords.has(tmpKey);
			tmpOutcomes.set(tmpKey, { Entity: tmpEntity, GUID: String(tmpGUID), Failed: tmpFailed });
		}
	}
	for (const tmpKey of tmpFailedRecords)
	{
		if (!tmpOutcomes.has(tmpKey))
		{
			// Entity names carry no spaces; GUIDs can, so only the first space separates them.
			const tmpSeparator = tmpKey.indexOf(' ');
			tmpOutcomes.set(tmpKey, { Entity: tmpKey.substring(0, tmpSeparator), GUID: tmpKey.substring(tmpSeparator + 1), Failed: true });
		}
	}
	return tmpOutcomes;
}

/**
 * Release every record the server logged as not applied, so the next save re-emits it.
 *
 * The baseline is folded before the PUT, so a record the server then refused already reads as written.
 * The server's `ExecutionLog` is the source of record for what landed: a record counts as unapplied when
 * its newest appearance under `OnSave` is a failure. Dropping its baseline makes it project whole again on
 * the next save, whenever that is. The log is only read here, never written.
 *
 * @param {Object} pPict - The pict instance.
 * @param {Record<string, any>|string} [pEntityData] - The document's `EntityData`, as an object or its JSON.
 * @return {number} - How many baseline records were released.
 */
function releaseUnappliedRecords(pPict, pEntityData)
{
	let tmpEntityData = pEntityData;
	if (typeof tmpEntityData === 'string')
	{
		try
		{
			tmpEntityData = JSON.parse(tmpEntityData);
		}
		catch (pError)
		{
			return 0;
		}
	}
	const tmpLog = (tmpEntityData && typeof tmpEntityData === 'object' && Array.isArray(tmpEntityData.ExecutionLog)) ? tmpEntityData.ExecutionLog : null;
	if (!tmpLog)
	{
		return 0;
	}
	const tmpContext = libScopedEntityData.DEFAULT_CONTEXT;
	const tmpLatest = new Map();
	for (const tmpEntry of tmpLog)
	{
		if (!tmpEntry || typeof tmpEntry !== 'object' || tmpEntry.TriggerKey !== tmpContext)
		{
			continue;
		}
		for (const [ tmpKey, tmpOutcome ] of readLogEntryOutcomes(tmpEntry))
		{
			tmpLatest.set(tmpKey, tmpOutcome);
		}
	}
	const tmpReleased = [ ];
	for (const tmpOutcome of tmpLatest.values())
	{
		if (tmpOutcome.Failed && dropBaselineRecord(pPict, tmpContext, tmpOutcome.Entity, tmpOutcome.GUID))
		{
			tmpReleased.push(`${tmpOutcome.Entity}/${tmpOutcome.GUID}`);
		}
	}
	if (tmpReleased.length > 0 && pPict && pPict.log && typeof pPict.log.info === 'function')
	{
		pPict.log.info(`EntityDataBaseline: the server did not apply ${tmpReleased.length} record(s); the next save sends them again: ${tmpReleased.join(', ')}.`);
	}
	return tmpReleased.length;
}

module.exports =
{
	BASELINE_ADDRESS,
	PROJECTION_SWEEP_EVENT,
	pruneContextRootToDelta,
	runProjectionSweepToScratch,
	captureBaselineNow,
	applyResolvedIdentities,
	isDocumentFrozen,
	resolveHeldIdentities,
	scheduleLoadBaselineCapture,
	DELETED_FIELD,
	isDirectiveField,
	isCrossEntityReferenceField,
	getEntityClassification,
	isIdentityField,
	isAuditField,
	isNumericField,
	isComparableField,
	isEmittableField,
	hasServerIdentity,
	isTombstoneRecord,
	valuesDiffer,
	computeRecordDelta,
	reduceRecordForBaseline,
	getBaselineStore,
	readBaselineRecord,
	writeBaselineRecord,
	dropBaselineRecord,
	captureBaselineFromSweep,
	refreshBaselineRecords,
	isAlwaysEmittedReference,
	classifyRecordRefresh,
	adoptRefreshedFields,
	foldEmissionIntoBaseline,
	clearBaseline,
	recordPendingEmission,
	foldPendingEmission,
	getPendingEmissionRoots,
	mergeObjectOverServer,
	mergePendingObjectFieldsWithServer,
	hasBaseline,
	collectHeldRecords,
	releaseUnappliedRecords,
};
